import React, { Dispatch, SetStateAction } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Input from "../../../componentLibrary/Input";
import Combobox from "../../../componentLibrary/Combobox";
import { ImageTypeEnum } from "../../../types";
import Button from "../../../componentLibrary/Button";
import { ImageExtension } from "../ImageManagementPage";

type ToolbarProps = {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  typeFilter: ImageTypeEnum;
  setTypeFilter: Dispatch<SetStateAction<ImageTypeEnum>>;
  extensionFilter: string;
  setExtensionFilter: Dispatch<SetStateAction<ImageExtension>>;
  handleAddImage: (type: ImageTypeEnum) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  canCreateContent?: boolean;
  /** When set, only this type is shown; type dropdown is hidden and a single Add button for this type is shown. */
  singleType?: ImageTypeEnum;
};

const ADD_BUTTON_CONFIG: Record<
  ImageTypeEnum,
  { label: string; variant: "secondary" | "primary" }
> = {
  [ImageTypeEnum.ICON]: { label: "Icon", variant: "secondary" },
  [ImageTypeEnum.ILLUSTRATION]: { label: "Illustration", variant: "primary" },
  [ImageTypeEnum.THUMBNAIL]: { label: "Thumbnail", variant: "primary" },
};

const Toolbar = ({
  searchTerm,
  setSearchTerm,
  typeFilter,
  setTypeFilter,
  extensionFilter,
  setExtensionFilter,
  handleAddImage,
  setPage,
  setLimit,
  canCreateContent = true,
  singleType,
}: ToolbarProps) => {
  const showTypeDropdown = singleType == null;
  const effectiveType = singleType ?? typeFilter;
  const addConfig =
    ADD_BUTTON_CONFIG[effectiveType] ?? ADD_BUTTON_CONFIG[ImageTypeEnum.ICON];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex flex-row justify-between items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <Input
            type="text"
            placeholder="Search images by name or alt text..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        {showTypeDropdown && (
          <div className="mt-2 w-40">
            <Combobox
              value={{ value: typeFilter, label: typeFilter }}
              onChange={(option) =>
                setTypeFilter(option.value as ImageTypeEnum)
              }
              options={[
                { value: ImageTypeEnum.ICON, label: "Icons" },
                { value: ImageTypeEnum.ILLUSTRATION, label: "Illustrations" },
              ]}
            />
          </div>
        )}
        <div className="mt-2 w-40">
          <Combobox
            value={{
              value: extensionFilter,
              label: extensionFilter?.slice(1)?.toUpperCase(),
            }}
            onChange={(option) =>
              setExtensionFilter(option.value as ImageExtension)
            }
            placeholder="Pick Image Extension"
            options={[
              { value: ".jpg", label: "JPG" },
              { value: ".png", label: "PNG" },
              { value: ".jpeg", label: "JPEG" },
              { value: ".gif", label: "GIF" },
              { value: ".svg", label: "SVG" },
              { value: ".webp", label: "WEBP" },
            ]}
          />
        </div>
        {showTypeDropdown ? (
          <>
            <Button
              variant="secondary"
              onClick={() =>
                canCreateContent && handleAddImage(ImageTypeEnum.ICON)
              }
              className="mt-2"
              disabled={!canCreateContent}
              title={
                !canCreateContent
                  ? "Ask an admin if you need permission to add images"
                  : "Add Icon"
              }
            >
              <PlusIcon className="h-4 w-4" />
              Icon
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                canCreateContent && handleAddImage(ImageTypeEnum.ILLUSTRATION)
              }
              className="mt-2"
              disabled={!canCreateContent}
              title={
                !canCreateContent
                  ? "Ask an admin if you need permission to add images"
                  : "Add Illustration"
              }
            >
              <PlusIcon className="h-4 w-4" />
              Illustration
            </Button>
          </>
        ) : (
          <Button
            variant={addConfig.variant}
            onClick={() => canCreateContent && handleAddImage(effectiveType)}
            className="mt-2"
            disabled={!canCreateContent}
            title={
              !canCreateContent
                ? "Ask an admin if you need permission to add images"
                : `Add ${addConfig.label}`
            }
          >
            <PlusIcon className="h-4 w-4" />
            {addConfig.label}
          </Button>
        )}
        <Button
          variant="cautionary"
          onClick={() => {
            setSearchTerm("");
            setTypeFilter(singleType ?? ImageTypeEnum.ICON);
            setExtensionFilter(null);
            setPage(1);
            setLimit(10);
          }}
          className="mt-2"
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default Toolbar;
